import { createTheme, type ThemeOptions } from '@mui/material/styles';
import type { PaletteColor } from '@mui/material/styles';
import { elevation, motion, palette, radius, spacing, typography, zIndex } from './tokens';

// Suppress TS unused-import: PaletteColor is referenced inside the
// `declare module` block below, which TS's noUnusedLocals doesn't see.
type _PaletteColorAlias = PaletteColor;
void ({} as _PaletteColorAlias);

/**
 * Wires design tokens into MUI's theme object so component-level styling
 * goes through `theme.palette.*` and we never end up with two competing
 * sources of truth. The theme is portal-agnostic — Platform Owner and
 * Tenant Owner share it; their navigation accents are layered on top via
 * `useAccent()` (see hooks/useAccent.ts in PR-B).
 */

declare module '@mui/material/styles' {
  interface Palette {
    brand: PaletteColor & { lighter: string };
    accent: PaletteColor & { lighter: string };
    surface: { canvas: string; card: string; elevated: string; sunken: string; inverse: string };
    border: { subtle: string; strong: string; focus: string };
    status: Record<'success' | 'successBg' | 'warning' | 'warningBg' | 'danger' | 'dangerBg' | 'info' | 'infoBg', string>;
  }
  interface PaletteOptions {
    brand?: Partial<PaletteColor & { lighter: string }>;
    accent?: Partial<PaletteColor & { lighter: string }>;
    surface?: { canvas?: string; card?: string; elevated?: string; sunken?: string; inverse?: string };
    border?: { subtle?: string; strong?: string; focus?: string };
    status?: Record<string, string>;
  }
  interface TypeText {
    tertiary: string;
    onInverse: string;
    onBrand: string;
  }
  interface Theme {
    elevation: typeof elevation;
    motion: typeof motion;
    radius: typeof radius;
  }
  interface ThemeOptions {
    elevation?: typeof elevation;
    motion?: typeof motion;
    radius?: typeof radius;
  }
}

const themeOptions: ThemeOptions = {
  spacing,
  zIndex: { ...zIndex },
  shape: { borderRadius: radius.md },
  palette: {
    mode: 'light',
    primary: { main: palette.brand.main, dark: palette.brand.dark, light: palette.brand.lighter, contrastText: palette.brand.contrastText },
    secondary: { main: palette.accent.main, dark: palette.accent.dark, light: palette.accent.lighter, contrastText: palette.accent.contrastText },
    brand: { main: palette.brand.main, dark: palette.brand.dark, light: palette.brand.lighter, lighter: palette.brand.lighter, contrastText: palette.brand.contrastText },
    accent: { main: palette.accent.main, dark: palette.accent.dark, light: palette.accent.lighter, lighter: palette.accent.lighter, contrastText: palette.accent.contrastText },
    background: { default: palette.surface.canvas, paper: palette.surface.card },
    text: { primary: palette.text.primary, secondary: palette.text.secondary, tertiary: palette.text.tertiary, onInverse: palette.text.onInverse, onBrand: palette.text.onBrand, disabled: palette.text.disabled },
    success: { main: palette.status.success },
    warning: { main: palette.status.warning },
    error: { main: palette.status.danger },
    info: { main: palette.status.info },
    surface: palette.surface,
    border: palette.border,
    status: palette.status,
    divider: palette.border.subtle,
  },
  typography: {
    fontFamily: typography.fontFamily,
    h1: { fontSize: typography.h1.size, lineHeight: typography.h1.lineHeight, fontWeight: typography.h1.weight, letterSpacing: typography.h1.tracking },
    h2: { fontSize: typography.h2.size, lineHeight: typography.h2.lineHeight, fontWeight: typography.h2.weight, letterSpacing: typography.h2.tracking },
    h3: { fontSize: typography.h3.size, lineHeight: typography.h3.lineHeight, fontWeight: typography.h3.weight, letterSpacing: typography.h3.tracking },
    body1: { fontSize: typography.bodyLg.size, lineHeight: typography.bodyLg.lineHeight, fontWeight: typography.bodyLg.weight },
    body2: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, fontWeight: typography.body.weight },
    caption: { fontSize: typography.caption.size, lineHeight: typography.caption.lineHeight, fontWeight: typography.caption.weight, letterSpacing: typography.caption.tracking },
    overline: { fontSize: typography.micro.size, lineHeight: typography.micro.lineHeight, fontWeight: typography.micro.weight, letterSpacing: typography.micro.tracking, textTransform: 'uppercase' },
    button: { fontWeight: 500, letterSpacing: 0, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.surface.canvas,
          color: palette.text.primary,
          fontFeatureSettings: '"cv11", "ss01", "ss03"',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*::selection': { backgroundColor: 'rgba(18, 122, 114, 0.18)' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: false },
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          padding: '8px 14px',
          transition: `background-color ${motion.durations.fast}ms ${motion.easings.standard}, box-shadow ${motion.durations.fast}ms ${motion.easings.standard}`,
          '&:focus-visible': { boxShadow: elevation.focusRing },
        },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', borderRadius: radius.md },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { boxShadow: elevation.card, borderRadius: radius.md },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.border.strong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: palette.border.focus },
        },
        notchedOutline: { borderColor: palette.border.subtle },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.surface.inverse,
          color: palette.text.onInverse,
          fontSize: 12,
          padding: '6px 10px',
          borderRadius: radius.xs,
        },
        arrow: { color: palette.surface.inverse },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.pill, fontWeight: 500, fontSize: 12 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: palette.border.subtle },
        head: { fontWeight: 600, color: palette.text.secondary, textTransform: 'none' },
      },
    },
  },
  elevation,
  motion,
  radius,
};

export const theme = createTheme(themeOptions);
